import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import {
    LuBell,
    LuChevronDown,
    LuChevronRight,
    LuLifeBuoy,
    LuLogOut,
    LuMenu,
    LuMoon,
    LuPalette,
    LuSun,
    LuUser,
} from 'react-icons/lu';
import { getPageMeta } from '../config/navigation';
import { clearSession, getSession } from '../utils/auth';
import { originFromEvent, setTheme, useIsDark } from '../lib/theme';
import { cn } from '../utils/cn';
import Button, { fillClass } from './ui/button';

const MENU_LINKS = [
    { label: 'My Profile', icon: LuUser, to: '/profile' },
    { label: 'Notifications', icon: LuBell, to: '/notification' },
    { label: 'Theme Settings', icon: LuPalette, to: '/theme' },
    { label: 'Help & Support', icon: LuLifeBuoy, to: '/support' },
];

const ICON_BUTTON = cn(
    'inline-flex h-9 w-9 items-center justify-center rounded-lg text-slate-500 transition-colors duration-300',
    'before:bg-slate-100 hover:text-slate-900 dark:text-slate-400 dark:before:bg-slate-800 dark:hover:text-white',
    fillClass
);
const MENU_ITEM =
    'flex w-full items-center gap-3 rounded-lg px-2.5 py-2 text-sm text-slate-700 transition-colors hover:bg-slate-50 hover:text-slate-900 dark:text-slate-300 dark:hover:bg-slate-800 dark:hover:text-white';

const getInitials = (name = '') => {
    const parts = name.trim().split(/\s+/).filter(Boolean);
    if (parts.length === 0) return '';
    const last = parts.length > 1 ? parts[parts.length - 1][0] : '';
    return (parts[0][0] + last).toUpperCase();
};

const Avatar = ({ name, className }) => (
    <span
        aria-hidden="true"
        className={cn(
            'flex shrink-0 items-center justify-center rounded-full bg-gradient-to-br from-brand-600 to-fuchsia-600 font-semibold text-white',
            className
        )}
    >
        {getInitials(name)}
    </span>
);

function LogoutDialog({ loggingOut, onCancel, onConfirm }) {
    useEffect(() => {
        const onKey = (e) => e.key === 'Escape' && !loggingOut && onCancel();
        window.addEventListener('keydown', onKey);
        return () => window.removeEventListener('keydown', onKey);
    }, [loggingOut, onCancel]);

    return (
        <div
            className="fixed inset-0 z-[60] flex animate-fade-in items-center justify-center bg-slate-900/40 p-4 dark:bg-slate-950/70"
            onClick={() => !loggingOut && onCancel()}
        >
            <div
                role="alertdialog"
                aria-modal="true"
                aria-labelledby="logout-title"
                onClick={(e) => e.stopPropagation()}
                className="w-full max-w-sm animate-pop-in rounded-2xl bg-white p-6 shadow-xl dark:bg-slate-900 dark:ring-1 dark:ring-slate-800"
            >
                <span className="flex h-11 w-11 items-center justify-center rounded-full bg-rose-50 text-rose-600 dark:bg-rose-500/10 dark:text-rose-400">
                    <LuLogOut className="h-5 w-5" aria-hidden="true" />
                </span>
                <h3 id="logout-title" className="mt-4 text-lg font-semibold text-slate-900 dark:text-white">
                    Log out?
                </h3>
                <p className="mt-1 text-sm text-slate-500 dark:text-slate-400">
                    Your session data will be cleared. You'll need an OTP to sign in again.
                </p>
                <div className="mt-6 flex justify-end gap-2">
                    <Button variant="secondary" onClick={onCancel} disabled={loggingOut}>
                        Cancel
                    </Button>
                    <Button variant="danger" onClick={onConfirm} loading={loggingOut}>
                        {loggingOut ? 'Logging out…' : 'Log out'}
                    </Button>
                </div>
            </div>
        </div>
    );
}

function Header({ onOpenMobileNav }) {
    const location = useLocation();
    const { section, label } = getPageMeta(location);
    const session = useMemo(getSession, []);
    const name = session?.name || 'Admin';
    const email = session?.email || '';
    const isDark = useIsDark();

    const [menuOpen, setMenuOpen] = useState(false);
    const [confirmLogout, setConfirmLogout] = useState(false);
    const [loggingOut, setLoggingOut] = useState(false);
    const menuRef = useRef(null);

    // Close the user menu on route change, outside click, or Escape
    useEffect(() => {
        setMenuOpen(false);
    }, [location]);
    useEffect(() => {
        if (!menuOpen) return undefined;
        const onClick = (e) => !menuRef.current?.contains(e.target) && setMenuOpen(false);
        const onKey = (e) => e.key === 'Escape' && setMenuOpen(false);
        document.addEventListener('mousedown', onClick);
        window.addEventListener('keydown', onKey);
        return () => {
            document.removeEventListener('mousedown', onClick);
            window.removeEventListener('keydown', onKey);
        };
    }, [menuOpen]);

    const handleConfirmLogout = () => {
        setLoggingOut(true);
        setTimeout(() => {
            clearSession();
            window.location.href = '/login';
        }, 500);
    };

    // The new theme spreads out in a circle from the clicked toggle
    const toggleTheme = (event) => setTheme(isDark ? 'light' : 'dark', originFromEvent(event));
    const isLive = location.pathname === '/transaction/live';

    return (
        <>
            <header className="sticky top-0 z-30 border-b border-slate-200/80 bg-white dark:border-slate-800 dark:bg-slate-900">
                <div className="flex h-16 items-center gap-3 px-4 sm:px-6 lg:px-8">
                    <button
                        type="button"
                        onClick={onOpenMobileNav}
                        aria-label="Open menu"
                        className={cn(ICON_BUTTON, '-ml-1.5 shrink-0 text-slate-600 lg:hidden dark:text-slate-300')}
                    >
                        <LuMenu className="h-5 w-5" />
                    </button>

                    <nav aria-label="Breadcrumb" className="flex min-w-0 items-center gap-1.5 text-sm">
                        {section && (
                            <>
                                <span className="hidden truncate text-slate-500 sm:inline dark:text-slate-400">{section}</span>
                                <LuChevronRight
                                    className="hidden h-4 w-4 shrink-0 text-slate-300 sm:block dark:text-slate-600"
                                    aria-hidden="true"
                                />
                            </>
                        )}
                        <span className="truncate font-semibold text-slate-900 dark:text-white">{label}</span>
                    </nav>

                    <div className="ml-auto flex shrink-0 items-center gap-1 sm:gap-2">
                        <Link
                            to="/transaction/live"
                            className={cn(
                                'inline-flex h-8 items-center gap-2 rounded-full border px-3 text-xs font-semibold transition-colors',
                                isLive
                                    ? 'border-emerald-300 bg-emerald-100 text-emerald-800 dark:border-emerald-400/40 dark:bg-emerald-500/20 dark:text-emerald-200'
                                    : 'border-emerald-200 bg-emerald-50 text-emerald-700 hover:bg-emerald-100 dark:border-emerald-500/30 dark:bg-emerald-500/10 dark:text-emerald-300 dark:hover:bg-emerald-500/20'
                            )}
                        >
                            <span className="relative flex h-2 w-2" aria-hidden="true">
                                <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-emerald-400 opacity-75" />
                                <span className="relative inline-flex h-2 w-2 rounded-full bg-emerald-500" />
                            </span>
                            Live
                        </Link>

                        <button
                            type="button"
                            onClick={toggleTheme}
                            aria-label={isDark ? 'Switch to light mode' : 'Switch to dark mode'}
                            title={isDark ? 'Light mode' : 'Dark mode'}
                            className={cn(ICON_BUTTON, 'hidden sm:inline-flex')}
                        >
                            {isDark ? <LuSun className="h-5 w-5" /> : <LuMoon className="h-5 w-5" />}
                        </button>

                        <Link to="/notification" aria-label="Notifications" className={cn(ICON_BUTTON, 'relative')}>
                            <LuBell className="h-5 w-5" />
                            <span className="absolute right-2 top-2 h-2 w-2 rounded-full bg-rose-500 ring-2 ring-white dark:ring-slate-900" />
                        </Link>

                        <div ref={menuRef} className="relative">
                            <button
                                type="button"
                                onClick={() => setMenuOpen((open) => !open)}
                                aria-label={`Account menu for ${name}`}
                                aria-haspopup="menu"
                                aria-expanded={menuOpen}
                                className="flex items-center gap-2 rounded-lg p-1 transition-colors hover:bg-slate-100 md:pr-2 dark:hover:bg-slate-800"
                            >
                                <Avatar name={name} className="h-8 w-8 text-xs" />
                                <span className="hidden text-left leading-tight md:block">
                                    <span className="block max-w-[10rem] truncate text-sm font-medium text-slate-800 dark:text-slate-100">
                                        {name}
                                    </span>
                                    <span className="block text-xs text-slate-500 dark:text-slate-400">Administrator</span>
                                </span>
                                <LuChevronDown
                                    aria-hidden="true"
                                    className={cn(
                                        'hidden h-4 w-4 text-slate-400 transition-transform md:block dark:text-slate-500',
                                        menuOpen && 'rotate-180'
                                    )}
                                />
                            </button>

                            {menuOpen && (
                                <div
                                    role="menu"
                                    className="absolute right-0 mt-2 w-64 origin-top-right animate-pop-in rounded-xl border border-slate-200 bg-white p-1.5 shadow-lg dark:border-slate-700 dark:bg-slate-900 dark:shadow-black/40"
                                >
                                    <div className="flex items-center gap-3 px-2.5 py-2.5">
                                        <Avatar name={name} className="h-10 w-10 text-sm" />
                                        <div className="min-w-0">
                                            <p className="truncate text-sm font-semibold text-slate-900 dark:text-white">{name}</p>
                                            <p className="truncate text-xs text-slate-500 dark:text-slate-400">{email}</p>
                                        </div>
                                    </div>
                                    <div className="my-1 h-px bg-slate-100 dark:bg-slate-800" />
                                    {MENU_LINKS.map(({ label: text, icon: Icon, to }) => (
                                        <Link key={to} to={to} role="menuitem" className={MENU_ITEM}>
                                            <Icon className="h-4 w-4 text-slate-400 dark:text-slate-500" aria-hidden="true" />
                                            {text}
                                        </Link>
                                    ))}
                                    <button
                                        type="button"
                                        role="menuitemcheckbox"
                                        aria-checked={isDark}
                                        onClick={toggleTheme}
                                        className={MENU_ITEM}
                                    >
                                        <LuMoon className="h-4 w-4 text-slate-400 dark:text-slate-500" aria-hidden="true" />
                                        Dark mode
                                        <span
                                            aria-hidden="true"
                                            className={cn(
                                                'ml-auto inline-flex h-5 w-9 items-center rounded-full p-0.5 transition-colors',
                                                isDark ? 'bg-brand-500' : 'bg-slate-200'
                                            )}
                                        >
                                            <span
                                                className={cn(
                                                    'h-4 w-4 rounded-full bg-white shadow transition-transform duration-200',
                                                    isDark && 'translate-x-4'
                                                )}
                                            />
                                        </span>
                                    </button>
                                    <div className="my-1 h-px bg-slate-100 dark:bg-slate-800" />
                                    <button
                                        type="button"
                                        role="menuitem"
                                        onClick={() => {
                                            setMenuOpen(false);
                                            setConfirmLogout(true);
                                        }}
                                        className="flex w-full items-center gap-3 rounded-lg px-2.5 py-2 text-sm text-rose-600 transition-colors hover:bg-rose-50 dark:text-rose-400 dark:hover:bg-rose-500/10"
                                    >
                                        <LuLogOut className="h-4 w-4" aria-hidden="true" />
                                        Log out
                                    </button>
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            </header>

            {confirmLogout && (
                <LogoutDialog
                    loggingOut={loggingOut}
                    onCancel={() => setConfirmLogout(false)}
                    onConfirm={handleConfirmLogout}
                />
            )}
        </>
    );
}

export default Header;
